import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UserTasklistService } from 'src/app/services/user-tasklist.service';
import { ConfigService } from 'src/app/services/config.service';
import { SvgIconComponent } from 'src/app/components/svg-icon/svg-icon.component';
import { LoggingService } from 'src/app/services/logging.service';
import {
  DxDataGridComponent,
  DxDataGridModule,
  DxTextBoxModule,
  DxSwitchModule,
  DxButtonModule,
  DxDropDownButtonModule,
} from 'devextreme-angular';
import { AppGlobals } from 'src/app/app.globals';
import { PrioritiesLookup } from 'src/app/constants/Priority';
import { PriorityPillComponent } from 'src/app/components/priority-pill/priority-pill.component';
import { BehaviorSubject } from 'rxjs';
import { Workbook } from 'exceljs';
import { exportDataGrid } from 'devextreme/excel_exporter';

import { ActivityLogService } from 'src/app/services/activity-log.service';
import { ActivitySection } from 'src/app/constants/activity-log';
enum TasklistViewState {
  MyTasks = 'MY_TASKS',
  DepartmentTasks = 'DEPARTMENT_TASKS',
}

export interface filterButtonData {
  id: number;
  text: string;
  hasStatusDot: boolean;
  statusId?: number; // Added statusId for filtering
  count: number;
  statusFilterValue?: any; // Optional, used for filtering
}

@Component({
    selector: 'app-user-tasklist',
    imports: [
        CommonModule,
        DxDataGridModule,
        PriorityPillComponent,
        DxTextBoxModule,
        DxSwitchModule,
        DxButtonModule,
        DxDropDownButtonModule,
        SvgIconComponent,
    ],
    templateUrl: './user-tasklist.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./user-tasklist.component.css']
})
export class UserTasklistComponent implements OnInit {
  @ViewChild(DxDataGridComponent, { static: false }) dataGrid: DxDataGridComponent;

  userTasks: any[] = [];
  priorityLookup: typeof PrioritiesLookup = PrioritiesLookup;

  filterByMyTasks: boolean = true;
  filterByDepartmentTasks: boolean = false;

  usermail: string = AppGlobals.LoggedInUserData.email;

  // #4994: "My tasks" = held by me or by my department. Precomputed per row (isMine) so the grid
  // filter, the counts and the toggle all agree.
  private readonly myTasksFilter = ['isMine', '=', true];

  filterButtonData: filterButtonData[] = [
    { id: 1, text: $localize`:@@all:All`, hasStatusDot: false, count: 0, statusFilterValue: null },
    {
      id: 2,
      text: $localize`:@@deadlineOverdue:Deadline Overdue`,
      hasStatusDot: true,
      statusId: 1,
      count: 0,
      statusFilterValue: ['groupId', '=', 1],
    },
    {
      id: 3,
      text: $localize`:@@deadlineDueSoon:Deadline Due Soon`,
      hasStatusDot: true,
      statusId: 2,
      count: 0,
      statusFilterValue: ['groupId', '=', 2],
    },
    {
      id: 4,
      text: $localize`:@@deadlineToday:Deadline Far Away`,
      hasStatusDot: true,
      statusId: 3,
      count: 0,
      statusFilterValue: ['groupId', '=', 3],
    },
  ];

  statusDotColors: { [key: number]: string } = {
    1: '#C20909', // Overdue
    2: '#F5B937', // Due Soon
    3: '#629D37', // Far Away
  };

  priorityHeaderFilter = [
    {
      text: $localize`:@@critical:Critical`,
      value: 0,
    },
    {
      text: $localize`:@@high:High`,
      value: 1,
    },
    {
      text: $localize`:@@medium:Medium`,
      value: 2,
    },
    {
      text: $localize`:@@low:Low`,
      value: 3,
    },
  ];

  searchExpr$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>('');
  currentDepartmentFilter$: BehaviorSubject<any[] | null> = new BehaviorSubject<any[] | null>(this.myTasksFilter);
  currentStatusFilter$: BehaviorSubject<string[] | null> = new BehaviorSubject<string[] | null>(null);

  // "Aktuelle Aufgaben" toggle (work item #4523) — filters to tasks whose task
  // package is currently active or overdue. Off by default; state persists per
  // browser via localStorage, following the same raw-key pattern the sibling
  // Project Tasks page used for its equivalent filter.
  showCurrentTasksOnly: boolean = localStorage.getItem('userTasklistCurrentTasksSwitch') === 'true';
  currentActiveOrOverdueFilter$: BehaviorSubject<any[] | null> = new BehaviorSubject<any[] | null>(
    this.showCurrentTasksOnly ? ['isCurrentOrOverdue', '=', true] : null
  );

  toggleVisible: boolean = false;

  activeStatusFilterId: number = 1;

  constructor(
    private userTasklistService: UserTasklistService,
    private configService: ConfigService,
    private loggingService: LoggingService,
    private appGlobals: AppGlobals,
    private activityLog: ActivityLogService
  ) {}

  tasklistViewState: TasklistViewState = TasklistViewState.MyTasks;
  TasklistViewState = TasklistViewState; // for template access

  async ngOnInit(): Promise<void> {
    this.activityLog.logView({ Section: ActivitySection.USER_TASKS });
    this.appGlobals.$headerTitle.next($localize`:@@userTasklist:User Tasklist`);

    this.currentDepartmentFilter$.subscribe(() => {
      this.setGridFilter();
      this.applySearchFilter(); // Apply search filter on department filter change
      this.updateFilterButtonCounts(); // Update filter button counts on department filter change
    });

    this.currentStatusFilter$.subscribe(() => {
      this.setGridFilter();
      this.applySearchFilter(); // Apply search filter on status filter change
    });

    this.searchExpr$.subscribe(() => {
      this.applySearchFilter(); // Apply search filter on search expression change
      this.updateFilterButtonCounts(); // Update filter button counts on search expression change
    });

    this.currentActiveOrOverdueFilter$.subscribe(() => {
      this.setGridFilter();
      this.applySearchFilter();
      this.updateFilterButtonCounts();
    });

    await this.loadTasklist();

    this.initializeFilterButtonCounts();
  }

  onUserTaskGridInitialized($event: any): void {
    this.setGridFilter();
    this.updateFilterButtonCounts();
  }

  onCellPrepared(e: any) {
    //set font weight to bold for header cells
    if (e.rowType === 'header') {
      e.cellElement.style.fontWeight = 'bold';
      e.cellElement.style.color = '#000000'; // Optional: Set a background color for header cells
    }
    if ((e.columnIndex == 0 && e.rowType == 'header') || (e.columnIndex == 2 && e.rowType == 'header')) {
      let target = e.cellElement.childNodes[0].nextSibling as HTMLElement;
      target.style.display = 'none';
    }
  }

  applySearchFilter(): void {
    const searchValue: string | null = this.searchExpr$.getValue();
    console.log('Applying Search Filter: ', searchValue);
    this.dataGrid?.instance.searchByText(searchValue);
  }

  async loadTasklist(): Promise<void> {
    const [result, users]: any[] = await Promise.all([
      this.userTasklistService.getUserTasklist({}),
      this.configService.getListOfUsers({}).catch(() => null),
    ]);
    if (result.hasError === true) {
      AppGlobals.ShowErrorMessage(result.feedback);
      return;
    }
    const me = this.usermail?.toLowerCase();
    const myDepartmentId = users?.projectsUsers?.find((u: any) => u.emailAddress?.toLowerCase() === me)?.departmentId ?? null;
    this.userTasks = UserTasklistComponent.withHolder(result.userTasklistItems ?? [], this.usermail, myDepartmentId);
    this.checkToggleVisibility();
  }

  /**
   * #4994: adds holderName (the department name for a department-held task, else the person) and
   * isMine. Every row reaches this list through a holder, so an empty holder cannot occur here.
   */
  static withHolder(items: any[], usermail: string, myDepartmentId: number | null): any[] {
    return items.map(item => ({
      ...item,
      holderName:
        item.assignedDepartmentId != null
          ? item.assignedDepartmentName
          : [item.firstname, item.lastname].filter(Boolean).join(' '),
      isMine:
        item.assignedDepartmentId != null
          ? myDepartmentId != null && item.assignedDepartmentId === myDepartmentId
          : item.usermail === usermail,
    }));
  }

  onToggleMyTasks(): void {
    this.tasklistViewState = TasklistViewState.MyTasks;
    this.currentDepartmentFilter$.next(this.myTasksFilter);
  }

  onToggleDepartmentTasks(): void {
    this.tasklistViewState = TasklistViewState.DepartmentTasks;
    this.currentDepartmentFilter$.next(null);
  }

  toggleMyTasksFilter(id: number, statusFilterValue?: string[]): void {
    this.activeStatusFilterId = id;
    this.currentStatusFilter$.next(statusFilterValue);
  }

  toggleCurrentTasksFilter(): void {
    localStorage.setItem('userTasklistCurrentTasksSwitch', JSON.stringify(this.showCurrentTasksOnly));
    this.currentActiveOrOverdueFilter$.next(this.showCurrentTasksOnly ? ['isCurrentOrOverdue', '=', true] : null);
  }

  onRowClick(e: any): void {
    if (e.data?.gridConnectionId && e.data?.projectId && e.data?.id) {
      const url = `site/${e.data.gridConnectionId}/project/${e.data.projectId}/tasks/${e.data.id}`;
      window.open(url, '_blank');
    }
  }

  setGridFilter(): void {
    const filters: any[][] = [
      this.currentDepartmentFilter$.getValue(),
      this.currentStatusFilter$.getValue(),
      this.currentActiveOrOverdueFilter$.getValue(),
    ].filter((f): f is any[] => f != null);

    if (filters.length === 0) {
      this.dataGrid?.instance.clearFilter(); // Clear the filter if none are applied
      return;
    }

    const combined: any[] = filters.reduce((acc: any[], f) => (acc.length === 0 ? [f] : [...acc, 'and', f]), []);
    this.dataGrid?.instance.filter(combined);
  }

  initializeFilterButtonCounts(): void {
    // Matches setGridFilter()'s default department scope (my tasks) plus the
    // "Aktuelle Aufgaben" toggle if it was restored from localStorage as on —
    // overwritten almost immediately by updateFilterButtonCounts() once the grid
    // initializes, but keeps the very first render from flashing wrong counts.
    const relevantTasks = this.userTasks.filter(
      (task: any) => task.isMine && (!this.showCurrentTasksOnly || task.isCurrentOrOverdue)
    );
    relevantTasks.forEach((task: any) => {
      this.filterButtonData.forEach(button => {
        if (button.statusFilterValue && task.groupId == button.statusFilterValue[2]) {
          button.count++;
        }
      });
    });
    this.filterButtonData[0].count = relevantTasks.length;
  }

  updateFilterButtonCounts(): void {
    const departmentFilter: any[] | null = this.currentDepartmentFilter$.getValue();
    const statusFilter: string[] | null = this.currentStatusFilter$.getValue();
    const currentTasksFilter: any[] | null = this.currentActiveOrOverdueFilter$.getValue();
    const searchExpr: string | null = this.searchExpr$.getValue();

    console.log('updateFilterButtonCounts called with:', {
      departmentFilter,
      statusFilter,
      currentTasksFilter,
      searchExpr,
    });

    // Deliberately does not fold in statusFilter (pre-existing behavior): counts
    // must reflect department + current-tasks + search, independent of which
    // deadline pill is selected, so each pill's own count stays meaningful.
    let filter: any[] = [
      ['datetime', 'contains', searchExpr],
      'or',
      ['taskTitle', 'contains', searchExpr],
      'or',
      ['projectTitle', 'contains', searchExpr],
    ];
    if (departmentFilter) {
      filter = [departmentFilter, 'and', filter];
    }
    if (currentTasksFilter) {
      filter = [currentTasksFilter, 'and', filter];
    }

    this.dataGrid?.instance
      .getDataSource()
      ?.store()
      //if filter is empty, dont filter
      .load({ filter })
      .then((result: any) => {
        console.log('updateFilterButtonCounts: ', result);
        const data: any[] = Array.isArray(result) ? result : result.data; // Ensure we extract the data array
        this.filterButtonData.forEach(button => {
          if (button.statusFilterValue) {
            button.count = data.filter((item: any) => item.groupId === button.statusId).length;
          } else {
            button.count = data.length; // For the "All" button, count all items
          }
        });
      })
      .catch((error: any) => {
        console.error('Error updating filter button counts:', error);
      });
  }

  checkToggleVisibility(): void {
    // #4994: department rows carry no usermail, so "someone else's task exists" is the test.
    this.toggleVisible = this.userTasks.some(task => !task.isMine);
  }

  downloadDocumentOptions: any = [{ value: -1, name: $localize`:@@export:Export`, icon: 'exportxlsx' }];

  handleExportButtonClick(e: any): void {
    if (e.itemData.value === -1) this.exportAsExcel();
  }

  async exportAsExcel(): Promise<void> {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('DepartmentTasks');

    await exportDataGrid({
      component: this.dataGrid.instance,
      worksheet,
      autoFilterEnabled: true,
    });

    const buffer = await workbook.xlsx.writeBuffer();
    this.saveExcelBufferToFile(buffer, 'department-tasks.xlsx');
    this.loggingService.logFileExport(buffer, 'department-tasks.xlsx', {
      section: ActivitySection.USER_TASKS,
    });
  }

  saveExcelBufferToFile(buffer: any, filename: string): void {
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    window.URL.revokeObjectURL(url);
  }
}
